import React, { useState, useEffect, useCallback } from "react";
import { FiLock, FiEye, FiEyeOff, FiKey, FiCopy, FiCheck, FiClock, FiInfo, FiTrash2, FiAlertTriangle } from "react-icons/fi";
import { HiOutlineInformationCircle, HiOutlineKey, HiOutlineLockClosed } from "react-icons/hi";
import {
  getProgramCredentials,
  ProgramVaultLink,
  consumeAccess,
  logFailedDecrypt,
  checkRateLimitStatus,
  RateLimitStatus
} from "../../utils/api/endpoints/vault";
import { decryptCredential, DecryptedCredential } from "../../utils/crypto/vault-crypto";
import toast from "react-hot-toast";

interface ProgramCredentialViewerProps {
  programId: number;
}

/**
 * ProgramCredentialViewer - Allows researchers/pentesters to view credentials
 * linked to a program using the access password (not master password)
 */
const ProgramCredentialViewer: React.FC<ProgramCredentialViewerProps> = ({ programId }) => {
  const [vaultLinks, setVaultLinks] = useState<ProgramVaultLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Access password state
  const [accessPassword, setAccessPassword] = useState("");
  const [showAccessPassword, setShowAccessPassword] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [selectedVaultLinkId, setSelectedVaultLinkId] = useState<number | null>(null);

  // Decrypted credentials
  const [decryptedEntries, setDecryptedEntries] = useState<Map<number, DecryptedCredential>>(new Map());
  const [showPassword, setShowPassword] = useState<Map<number, boolean>>(new Map());
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // Rate limiting state
  const [rateLimitStatus, setRateLimitStatus] = useState<Map<number, RateLimitStatus>>(new Map());
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getProgramCredentials(programId);
      setVaultLinks(response.credentials || []);
    } catch (err: any) {
      setError(err.message || "Failed to load credentials");
    } finally {
      setIsLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleDecrypt = async (vaultLink: ProgramVaultLink) => {
    if (!accessPassword) {
      toast.error("Please enter the access password");
      return;
    }

    setIsDecrypting(true);
    setSelectedVaultLinkId(vaultLink.linkId);
    setRateLimitError(null);

    try {
      // Check rate limit status first
      const status = await checkRateLimitStatus(vaultLink.vault.vault_id);
      if (status.isLocked) {
        const lockoutEnds = status.lockoutEndsAt ? new Date(status.lockoutEndsAt) : null;
        const minutesRemaining = lockoutEnds
          ? Math.ceil((lockoutEnds.getTime() - Date.now()) / 60000)
          : 30;
        setRateLimitError(`Account temporarily locked due to too many failed attempts. Try again in ${minutesRemaining} minutes.`);
        toast.error(`Too many failed attempts. Try again in ${minutesRemaining} minutes.`);
        return;
      }

      // Check access status
      if (vaultLink.accessStatus === "expired") {
        toast.error("This credential access has expired");
        return;
      }
      if (vaultLink.accessStatus === "consumed") {
        toast.error("You have already viewed this credential (view once limit reached)");
        return;
      }

      // Decrypt each entry with access password
      const newDecrypted = new Map(decryptedEntries);
      let decryptSuccess = false;
      let decryptError: Error | null = null;

      for (const entry of vaultLink.entries) {
        try {
          const decrypted = await decryptCredential(
            {
              encrypted_data: entry.encryptedData,
              iv: entry.iv,
              auth_tag: entry.authTag,
              salt: vaultLink.accessSalt || "",
              key_iterations: 600000
            },
            accessPassword
          );
          newDecrypted.set(entry.id, decrypted);
          decryptSuccess = true;
        } catch (err) {
          console.error(`Failed to decrypt entry ${entry.id}:`, err);
          decryptError = err as Error;
        }
      }

      // If all entries failed to decrypt, log failed attempt and show error
      if (!decryptSuccess && decryptError) {
        // Log failed decrypt attempt - this will track rate limiting
        try {
          const logResult = await logFailedDecrypt(vaultLink.vault.vault_id, programId, "Invalid access password");

          // Check if rate limited or locked out
          if (logResult.rateLimitInfo) {
            const { failureCount, lockedOut, lockoutDurationMs } = logResult.rateLimitInfo;
            if (lockedOut) {
              const lockoutMinutes = Math.ceil((lockoutDurationMs || 1800000) / 60000);
              setRateLimitError(`Too many failed attempts (${failureCount}). Account locked for ${lockoutMinutes} minutes.`);
              toast.error(`Account locked due to too many failed attempts. Try again in ${lockoutMinutes} minutes.`);
            } else if (failureCount >= 3) {
              toast.error(`Invalid password. ${5 - failureCount} attempts remaining before lockout.`);
            } else {
              toast.error("Invalid access password");
            }
          } else {
            toast.error("Invalid access password");
          }
        } catch (rateLimitErr: any) {
          // Handle rate limit error thrown from logFailedDecrypt
          if (rateLimitErr.rateLimited) {
            const minutes = Math.ceil((rateLimitErr.retryAfterMs || 900000) / 60000);
            setRateLimitError(rateLimitErr.lockedOut
              ? `Account locked. Try again in ${minutes} minutes.`
              : `Too many attempts. Please wait ${minutes} minutes.`);
            toast.error(rateLimitErr.message);
          } else {
            toast.error("Invalid access password");
          }
        }
        return;
      }

      setDecryptedEntries(newDecrypted);

      // Record access via consumeAccess for ALL access types (this logs to audit trail)
      // The backend handles different access types appropriately
      try {
        const accessResult = await consumeAccess(vaultLink.vault.vault_id, programId);

        // Update local state based on access type
        if (vaultLink.accessType === "view_once") {
          setVaultLinks(prev => prev.map(link =>
            link.linkId === vaultLink.linkId
              ? { ...link, accessStatus: "consumed" as const }
              : link
          ));
          toast.success("Credentials decrypted. This was a one-time view - access has been consumed.");
        } else {
          toast.success("Credentials decrypted successfully");
        }
      } catch (err) {
        console.error("Failed to record access:", err);
        toast.success("Credentials decrypted successfully");
      }
    } catch (err: any) {
      // Log failed decrypt attempt for security audit
      await logFailedDecrypt(vaultLink.vault.vault_id, programId, err.message || "Decryption failed");
      toast.error("Invalid access password");
      console.error("Decryption error:", err);
    } finally {
      setIsDecrypting(false);
      setSelectedVaultLinkId(null);
    }
  };

  const copyToClipboard = async (text: string, entryId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(entryId);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const toggleShowPassword = (entryId: number) => {
    setShowPassword(prev => new Map(prev).set(entryId, !prev.get(entryId)));
  };

  const getAccessBadge = (link: ProgramVaultLink) => {
    if (link.vaultDeleted || link.accessStatus === "deleted") {
      return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded flex items-center gap-1">
        <FiTrash2 className="w-3 h-3" />
        Vault Deleted
      </span>;
    }
    if (link.accessStatus === "expired") {
      return <span className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded">Expired</span>;
    }
    if (link.accessStatus === "consumed") {
      return <span className="px-2 py-1 text-xs bg-orange-100 text-orange-600 rounded">Already Viewed</span>;
    }
    if (link.accessType === "view_once") {
      return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-600 rounded">View Once</span>;
    }
    if (link.accessType === "time_limited") {
      return <span className="px-2 py-1 text-xs bg-purple-100 text-purple-600 rounded flex items-center gap-1">
        <FiClock className="w-3 h-3" />
        Until {new Date(link.expiresAt!).toLocaleDateString()}
      </span>;
    }
    return <span className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded">Unlimited</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-500">Loading credentials...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={fetchCredentials} className="mt-2 text-sm text-red-700 underline hover:no-underline">
          Try again
        </button>
      </div>
    );
  }

  if (vaultLinks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <FiLock className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <h3 className="text-sm font-medium text-gray-800">No Credentials Available</h3>
        <p className="text-xs text-gray-500 mt-1">No credentials have been shared for this program.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Access Password Input */}
      <div className="bg-[#FBFDFB] border border-[#EEF2F0] rounded-xl p-5 mb-3">
        <div className="flex items-start gap-4">
          <HiOutlineInformationCircle className="w-5 h-5 text-[#76D301] flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-[15px] font-bold text-slate-900 mb-0.5">Enter Access Password</h3>
            <p className="text-[13px] text-slate-400">
              The program owner should have shared an access password with you.
              This is different from their master password.
            </p>
          </div>
        </div>
      </div>

      <div className="relative mb-8">
        <div className="absolute left-4 top-1/2 -translate-y-1/2">
          <HiOutlineKey className="w-5 h-5 text-slate-300" />
        </div>
        <input
          type={showAccessPassword ? "text" : "password"}
          value={accessPassword}
          onChange={(e) => setAccessPassword(e.target.value)}
          className="w-full pl-12 pr-12 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-800 placeholder:text-slate-300"
          placeholder="Enter access password to unlock vault"
          disabled={!!rateLimitError}
        />
        <button
          type="button"
          onClick={() => setShowAccessPassword(!showAccessPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showAccessPassword ? <FiEyeOff size={18} /> : <FiEye size={18} className="text-slate-300" />}
        </button>
      </div>

      {/* Rate Limit Warning */}
      {rateLimitError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 mb-6">
          <FiAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-700">Access Temporarily Restricted</p>
            <p className="text-xs text-red-600 mt-1">{rateLimitError}</p>
            <button
              onClick={() => setRateLimitError(null)}
              className="text-xs font-bold text-red-700 underline mt-2 hover:text-red-800"
            >
              Dismiss and try again
            </button>
          </div>
        </div>
      )}

      {/* Vault Links */}
      {vaultLinks.map((link) => (
        <div key={link.linkId} className={`border rounded-lg overflow-hidden mb-6 ${link.vaultDeleted ? 'opacity-60' : ''}`}>
          {/* Vault Header */}
          <div className={`px-5 py-4 flex items-center justify-between border-b ${link.vaultDeleted ? 'bg-gray-100' : 'bg-[#000111]'}`}>
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${link.vaultDeleted ? 'bg-gray-200 border-gray-300' : 'bg-[#1E293B] border-white/5'}`}>
                {link.vaultDeleted ? (
                  <FiTrash2 className="w-4 h-4 text-gray-400" />
                ) : (
                  <FiKey className="w-4 h-4 text-[#76D301]" />
                )}
              </div>
              <div className="flex flex-col">
                <h3 className={`text-[14px] font-bold tracking-tight ${link.vaultDeleted ? 'text-gray-500 line-through' : 'text-white'}`}>
                  {link.vault.name || "CTB Vault"}
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-5 py-2 rounded-lg border border-[#DDEFD2] text-[#76D301] text-[11px] font-bold tracking-tight bg-white">
                Unlimited
              </span>
              {link.accessStatus === "available" && !link.vaultDeleted && (
                <button
                  onClick={() => handleDecrypt(link)}
                  disabled={isDecrypting || !accessPassword}
                  className="px-6 py-2 text-[13px] font-bold text-black bg-[#76D301] rounded-lg hover:bg-[#86E411] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDecrypting && selectedVaultLinkId === link.linkId ? (
                    <>
                      <span className="animate-spin h-3 w-3 border-2 border-black border-t-transparent rounded-full" />
                      Unlocking...
                    </>
                  ) : (
                    <>
                      <FiLock className="w-4 h-4" />
                      Unlock
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Deleted Vault Message */}
          {link.vaultDeleted && (
            <div className="px-4 py-6 bg-gray-50 text-center">
              <FiTrash2 className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">This vault has been deleted</p>
              <p className="text-xs text-gray-500 mt-1">
                The credentials that were shared from this vault are no longer available.
                Please contact the program owner for updated credentials.
              </p>
            </div>
          )}

          {/* Entries */}
          {!link.vaultDeleted && (
            <div className="divide-y">
              {link.entries.length === 0 && link.accessStatus !== "available" ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {link.accessStatus === "expired" && "Access to these credentials has expired."}
                  {link.accessStatus === "consumed" && "You have already viewed these credentials (one-time access)."}
                </div>
              ) : link.entries.map((entry) => {
                const decrypted = decryptedEntries.get(entry.id);
                const isPasswordShown = showPassword.get(entry.id);

                // Extract all fields from decrypted entries
                const getDecryptedValue = (type: string) =>
                  decrypted?.entries?.find(e => e.type === type)?.value || "";

                const passwordValue = getDecryptedValue("password");
                const usernameValue = getDecryptedValue("username") || entry.username;
                const urlValue = getDecryptedValue("url") || entry.url;
                const emailValue = getDecryptedValue("email");

                return (
                  <div key={entry.id} className="p-4 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-slate-700">{entry.label}</h4>
                      <p className="text-[11px] text-slate-300 font-medium capitalize mt-0.5">{entry.credentialType?.replace(/_/g, " ")}</p>
                    </div>
                    {/* Show status messages only when NOT decrypted */}
                    {!decrypted && (
                      <div className="flex items-center gap-3">
                        {link.accessStatus === "available" && (
                          <span className="text-[12px] text-slate-300 font-medium tracking-tight">Enter password to unlock</span>
                        )}
                        <HiOutlineLockClosed className="w-4 h-4 text-slate-200" />
                      </div>
                    )}

                    {/* Decrypted Fields */}
                    {decrypted && (
                      <div className="flex-1 ml-8 space-y-2 bg-gray-50 rounded-lg p-3">
                        {/* URL */}
                        {urlValue && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 w-20">URL</span>
                            <div className="flex-1 flex items-center gap-2">
                              <a href={urlValue} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline truncate max-w-xs">
                                {urlValue}
                              </a>
                              <button onClick={() => copyToClipboard(urlValue, entry.id + 1000)}
                                className="text-gray-400 hover:text-gray-600">
                                <FiCopy size={12} />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Email */}
                        {emailValue && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 w-20">Email</span>
                            <div className="flex-1 flex items-center gap-2">
                              <span className="text-sm font-mono">{emailValue}</span>
                              <button onClick={() => copyToClipboard(emailValue, entry.id + 2000)}
                                className="text-gray-400 hover:text-gray-600">
                                <FiCopy size={12} />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Username */}
                        {usernameValue && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 w-20">Username</span>
                            <div className="flex-1 flex items-center gap-2">
                              <span className="text-sm font-mono">{usernameValue}</span>
                              <button onClick={() => copyToClipboard(usernameValue, entry.id + 3000)}
                                className="text-gray-400 hover:text-gray-600">
                                <FiCopy size={12} />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Password */}
                        {passwordValue && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 w-20">Password</span>
                            <div className="flex-1 flex items-center gap-2">
                              <span className="text-sm font-mono">
                                {isPasswordShown ? passwordValue : "••••••••••••"}
                              </span>
                              <button onClick={() => toggleShowPassword(entry.id)}
                                className="text-gray-400 hover:text-gray-600">
                                {isPasswordShown ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                              </button>
                              <button onClick={() => copyToClipboard(passwordValue, entry.id)}
                                className="text-gray-400 hover:text-gray-600">
                                {copiedId === entry.id ? (
                                  <FiCheck size={12} className="text-green-500" />
                                ) : (
                                  <FiCopy size={12} />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProgramCredentialViewer;
